import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Trigger + dialog that lets a buyer submit a 1-5 star review of a supplier.
 * Props: supplierId, supplierName, quoteId?, shipmentId?, onSubmitted(), trigger (ReactNode)
 */
export default function ReviewDialog({ supplierId, supplierName, quoteId, shipmentId, onSubmitted, trigger, testid = "review-dialog" }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", {
        supplier_id: supplierId, rating, title: title || null, body: body || null,
        quote_id: quoteId || null, shipment_id: shipmentId || null,
      });
      toast.success("Thanks — your review is live.");
      setOpen(false); setTitle(""); setBody(""); setRating(5);
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button size="sm" data-testid={`${testid}-open`}>Leave a review</Button>}</DialogTrigger>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Rate {supplierName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>How was your experience?</Label>
            <div className="mt-2 flex items-center gap-1" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n} type="button"
                  onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  data-testid={`review-star-${n}`}
                  className="p-1"
                  aria-label={`${n} star${n>1?'s':''}`}
                >
                  <Star className={`w-8 h-8 ${(hover || rating) >= n ? "text-primary fill-primary" : "text-muted-foreground/40"}`} />
                </button>
              ))}
              <span className="ml-2 text-sm text-muted-foreground">{rating}/5</span>
            </div>
          </div>
          <div>
            <Label htmlFor="rev-title">Headline (optional)</Label>
            <Input id="rev-title" data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Reliable quality, fast turnaround" maxLength={120} />
          </div>
          <div>
            <Label htmlFor="rev-body">Details (optional)</Label>
            <Textarea id="rev-body" data-testid="review-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share what worked well and what could be better." maxLength={1000} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="review-submit" onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Post review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
